import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  CardContent,
  Autocomplete
} from '@mui/material';
import {
  Save as SaveIcon,
  Science as TrialIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import { createClinicalTrial, getClinicalTrials } from '../../services/tumorBoardService';
import { CANCER_TYPES, CLINICAL_TRIAL_PHASES } from '../../types/enums';
import { PageHeader, ContentCard } from './oncologyStyles';

const ClinicalTrialForm = () => {
  const { trialId } = useParams();
  const navigate = useNavigate();
  const { defaultFacility } = useUserData();
  const isEditing = !!trialId;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    nctNumber: '',
    protocolNumber: '',
    title: '',
    shortTitle: '',
    phase: '',
    trialStatus: 'Recruiting',
    sponsor: '',
    principalInvestigatorName: '',
    targetEnrollment: '',
    cancerTypes: [],
    inclusionCriteria: [''],
    exclusionCriteria: [''],
    description: ''
  });

  // Load trial data if editing
  useEffect(() => {
    if (isEditing && defaultFacility?.id) {
      const loadTrial = async () => {
        try {
          setLoading(true);
          const result = await getClinicalTrials(defaultFacility.id, { trialId });
          if (result.data?.length > 0) {
            const trial = result.data[0];
            setFormData({
              nctNumber: trial.nctNumber || '',
              protocolNumber: trial.protocolNumber || '',
              title: trial.title || '',
              shortTitle: trial.shortTitle || '',
              phase: trial.phase || '',
              trialStatus: trial.trialStatus || 'Recruiting',
              sponsor: trial.sponsor || '',
              principalInvestigatorName: trial.principalInvestigatorName || '',
              targetEnrollment: trial.targetEnrollment || '',
              cancerTypes: trial.cancerTypes || [],
              inclusionCriteria: trial.eligibilityCriteria?.inclusion || [''],
              exclusionCriteria: trial.eligibilityCriteria?.exclusion || [''],
              description: trial.description || ''
            });
          }
        } catch (err) {
          console.error('Error loading trial:', err);
          setError('Failed to load trial data');
        } finally {
          setLoading(false);
        }
      };
      loadTrial();
    }
  }, [isEditing, trialId, defaultFacility?.id]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCriteriaChange = (type, index, value) => {
    const field = type === 'inclusion' ? 'inclusionCriteria' : 'exclusionCriteria';
    setFormData(prev => {
      const criteria = [...prev[field]];
      criteria[index] = value;
      return { ...prev, [field]: criteria };
    });
  };

  const addCriteria = (type) => {
    const field = type === 'inclusion' ? 'inclusionCriteria' : 'exclusionCriteria';
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeCriteria = (type, index) => {
    const field = type === 'inclusion' ? 'inclusionCriteria' : 'exclusionCriteria';
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.phase) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const trialData = {
        facilityId: defaultFacility.id,
        nctNumber: formData.nctNumber,
        protocolNumber: formData.protocolNumber,
        title: formData.title,
        shortTitle: formData.shortTitle,
        phase: formData.phase,
        trialStatus: formData.trialStatus,
        sponsor: formData.sponsor,
        principalInvestigatorName: formData.principalInvestigatorName,
        targetEnrollment: parseInt(formData.targetEnrollment) || 0,
        currentEnrollment: 0,
        cancerTypes: formData.cancerTypes,
        eligibilityCriteria: {
          inclusion: formData.inclusionCriteria.filter(c => c.trim()),
          exclusion: formData.exclusionCriteria.filter(c => c.trim())
        },
        description: formData.description
      };

      await createClinicalTrial(trialData);
      setSuccess(true);

      // Navigate back after short delay
      setTimeout(() => {
        navigate('/oncology/clinical-trials');
      }, 1500);
    } catch (err) {
      console.error('Error saving clinical trial:', err);
      setError(err.message || 'Failed to save clinical trial');
    } finally {
      setLoading(false);
    }
  };

  const trialStatuses = ['Recruiting', 'Active', 'Completed', 'Suspended', 'Terminated', 'Withdrawn'];

  return (
    <Box>
      <PageHeader
        title={isEditing ? 'Edit Clinical Trial' : 'New Clinical Trial'}
        subtitle="Register a clinical trial for patient enrollment"
        icon={<TrialIcon />}
        backTo="/oncology/clinical-trials"
      />

      <ContentCard>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Clinical trial {isEditing ? 'updated' : 'created'} successfully!
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Trial Identification */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Trial Identification
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="NCT Number"
                  value={formData.nctNumber}
                  onChange={(e) => handleChange('nctNumber', e.target.value)}
                  placeholder="NCT01234567"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Protocol Number"
                  value={formData.protocolNumber}
                  onChange={(e) => handleChange('protocolNumber', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Trial Phase</InputLabel>
                  <Select
                    value={formData.phase}
                    label="Trial Phase"
                    onChange={(e) => handleChange('phase', e.target.value)}
                  >
                    {CLINICAL_TRIAL_PHASES.map(phase => (
                      <MenuItem key={phase} value={phase}>{phase}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  required
                  label="Full Title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Short Title"
                  value={formData.shortTitle}
                  onChange={(e) => handleChange('shortTitle', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.trialStatus}
                    label="Status"
                    onChange={(e) => handleChange('trialStatus', e.target.value)}
                  >
                    {trialStatuses.map(status => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Sponsor & Investigator */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
                  Sponsor & Investigator
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Sponsor"
                  value={formData.sponsor}
                  onChange={(e) => handleChange('sponsor', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Principal Investigator"
                  value={formData.principalInvestigatorName}
                  onChange={(e) => handleChange('principalInvestigatorName', e.target.value)}
                />
              </Grid>

              {/* Enrollment */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
                  Enrollment
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Target Enrollment"
                  value={formData.targetEnrollment}
                  onChange={(e) => handleChange('targetEnrollment', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <Autocomplete
                  multiple
                  size="small"
                  options={CANCER_TYPES}
                  value={formData.cancerTypes}
                  onChange={(e, newValue) => handleChange('cancerTypes', newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Applicable Cancer Types" />
                  )}
                />
              </Grid>

              {/* Eligibility Criteria */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
                  Eligibility Criteria
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="success.main" gutterBottom>
                  Inclusion
                </Typography>
                {formData.inclusionCriteria.map((criterion, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={criterion}
                      onChange={(e) => handleCriteriaChange('inclusion', index, e.target.value)}
                      placeholder={`Criterion ${index + 1}`}
                    />
                    {formData.inclusionCriteria.length > 1 && (
                      <IconButton size="small" onClick={() => removeCriteria('inclusion', index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addCriteria('inclusion')}
                >
                  Add
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="error.main" gutterBottom>
                  Exclusion
                </Typography>
                {formData.exclusionCriteria.map((criterion, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={criterion}
                      onChange={(e) => handleCriteriaChange('exclusion', index, e.target.value)}
                      placeholder={`Criterion ${index + 1}`}
                    />
                    {formData.exclusionCriteria.length > 1 && (
                      <IconButton size="small" onClick={() => removeCriteria('exclusion', index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addCriteria('exclusion')}
                >
                  Add
                </Button>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Brief description of the trial..."
                />
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                  <Button 
                    variant="outlined"
                    onClick={() => navigate('/oncology/clinical-trials')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : (isEditing ? 'Update Trial' : 'Create Trial')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </ContentCard>
    </Box>
  );
};

export default ClinicalTrialForm;
